import type { PlayerId } from "./protocol.js";

export const FUSE_RUNNERS_ARENAS = [
  "sproutworks",
  "tidal-engine",
  "gust-gallery",
  "cloud-foundry",
  "cinder-crown",
] as const;

export type FuseRunnersArenaId = (typeof FUSE_RUNNERS_ARENAS)[number];
export type FuseRunnersPhase =
  | "waiting"
  | "countdown"
  | "battle"
  | "paused"
  | "roundResult"
  | "completed";
export type FuseSeatMarker = "circle" | "triangle" | "diamond" | "square";
export type FuseRunnersAiProfile = "Breezy" | "Sparky" | "Voltaic";
export type FuseDangerDirection = "up" | "right" | "down" | "left";
export type FusePowerUp =
  | "extra-charge"
  | "long-spark"
  | "quick-soles"
  | "bubble-shield"
  | "remote-spark"
  | "piercing-spark"
  | "kick-toe"
  | "hover-belt"
  | "sticky-soles"
  | "hot-fuse";

export type FuseRunnersInput =
  | { type: "fuseRunnersMove"; roundId: number; seq: number; x: number; y: number }
  | { type: "fuseRunnersBomb"; roundId: number; actionId: number }
  | { type: "fuseRunnersTrigger"; roundId: number; actionId: number };

export interface FuseCellSnapshot {
  x: number;
  y: number;
}

export interface FuseSeatSnapshot {
  seatId: number;
  playerId?: PlayerId;
  displayName: string;
  color: string;
  marker: FuseSeatMarker;
  aiControlled: boolean;
  aiProfile?: FuseRunnersAiProfile;
  connected: boolean;
  alive: boolean;
  x: number;
  y: number;
  points: number;
  roundWins: number;
  placement: number | null;
  bombCapacity: number;
  bombsPlaced: number;
  blastRange: number;
  speedStacks: number;
  shield: boolean;
  remote: boolean;
  piercing: boolean;
  kick: boolean;
  hoverRemainingMs: number;
  stickyRemainingMs: number;
  arrivalShieldMs: number;
  handoffRemainingMs: number;
  eliminations: number;
  nearMisses: number;
  breakablesDestroyed: number;
  powerUpsCollected: number;
}

export interface FuseBombSnapshot {
  id: number;
  ownerSeatId: number;
  x: number;
  y: number;
  remainingMs: number;
  range: number;
  remote: boolean;
  kickArmed: boolean;
}

export interface FuseBlastSnapshot {
  x: number;
  y: number;
  remainingMs: number;
  ownerSeatId: number;
}

export interface FusePickupSnapshot extends FuseCellSnapshot {
  type: FusePowerUp;
}

export interface FuseDangerCompassSnapshot {
  bombId: number;
  direction: FuseDangerDirection;
  remainingMs: number;
}

export interface FuseRunnersSnapshot {
  gameType: "fuse-runners";
  phase: FuseRunnersPhase;
  arenaId: FuseRunnersArenaId;
  arenaName: string;
  roundId: number;
  roundNumber: number;
  roundCount: number;
  timeRemainingMs: number;
  countdownMs: number;
  width: number;
  height: number;
  hardBlocks: FuseCellSnapshot[];
  breakables: FuseCellSnapshot[];
  hazardCells: FuseCellSnapshot[];
  suddenSparkCells: FuseCellSnapshot[];
  pickups: FusePickupSnapshot[];
  bombs: FuseBombSnapshot[];
  blasts: FuseBlastSnapshot[];
  seats: FuseSeatSnapshot[];
  localSeatId?: number;
  dangerCompass?: FuseDangerCompassSnapshot;
  canMove: boolean;
  canBomb: boolean;
  canTrigger: boolean;
  standby: boolean;
  joinMessage?: string;
  result?: FuseRunnersResult;
}

export interface FuseRunnersResult {
  standings: Array<{
    seatId: number;
    playerId?: PlayerId;
    displayName: string;
    points: number;
    roundWins: number;
    eliminations: number;
    nearMisses: number;
    selfEliminations: number;
    place: number;
  }>;
  participantNames: string[];
  completedAt: string;
}

export function fuseBeatWindowMs(initialRemainingMs: number): number {
  return Math.min(
    1_800,
    Math.max(600, Number.isFinite(initialRemainingMs) ? initialRemainingMs : 600),
  );
}

export function fuseBeatStage(remainingMs: number, beatWindowMs: number): 0 | 1 | 2 | 3 {
  if (remainingMs <= beatWindowMs * 0.25) return 3;
  if (remainingMs <= beatWindowMs * 0.5) return 2;
  if (remainingMs <= beatWindowMs * 0.75) return 1;
  return 0;
}

export function weightedComebackPowerUp(
  original: FusePowerUp,
  deterministicRoll: number,
): FusePowerUp {
  if (original === "extra-charge" || original === "long-spark") return original;
  const roll = Math.abs(Math.trunc(deterministicRoll)) % 5;
  if (roll === 4) return original;
  return roll % 2 === 0 ? "extra-charge" : "long-spark";
}

export function isFuseRunnersInput(value: unknown): value is FuseRunnersInput {
  if (!isRecord(value) || !Number.isSafeInteger(value.roundId) || (value.roundId as number) < 0) {
    return false;
  }
  if (value.type === "fuseRunnersMove") {
    return (
      Number.isSafeInteger(value.seq) &&
      (value.seq as number) >= 0 &&
      isUnitNumber(value.x) &&
      isUnitNumber(value.y) &&
      Math.hypot(value.x, value.y) <= 1.05
    );
  }
  return (
    (value.type === "fuseRunnersBomb" || value.type === "fuseRunnersTrigger") &&
    Number.isSafeInteger(value.actionId) &&
    (value.actionId as number) >= 0
  );
}

function isUnitNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= -1 && value <= 1;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
